import { Configuration, LogLevel, PublicClientApplication } from '@azure/msal-browser';

// Shared with app-magic / app-commercial — same Entra app registration so users
// sign in once across the fleet.
const TENANT_ID = import.meta.env.VITE_AZURE_TENANT_ID || 'd54794b1-f598-4c0f-a276-6039a39774ac';
const CLIENT_ID = import.meta.env.VITE_AZURE_CLIENT_ID || '1b0c305e-2019-4620-8e30-abadad2171aa';

export const MAJANS_TENANT_ID = TENANT_ID;

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error(message);
        else if (level === LogLevel.Warning) console.warn(message);
      },
    },
  },
};

export const loginRequest = {
  scopes: ['User.Read', `api://${CLIENT_ID}/access_as_user`],
};

export const apiScopes = {
  scopes: [`api://${CLIENT_ID}/access_as_user`],
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const AUTH_BYPASS = import.meta.env.VITE_AUTH_BYPASS === 'true';

// Guards so a token-renewal failure bounces the user to interactive sign-in at
// most once, and at most once per minute — prevents any redirect loop.
let tokenRedirectInFlight = false;
const TOKEN_REDIRECT_KEY = 'purchasing:tokenRedirectAt';

/**
 * Acquire an API access token for the active (Majans-tenant) account.
 *
 * Silent first; on failure — overwhelmingly the hidden-iframe renewal timing out
 * when the browser blocks third-party cookies — fall back to a top-level
 * interactive redirect, which is a first-party navigation and isn't subject to
 * that. Without this fallback a `timed_out` dead-ends every authed page.
 */
/** MSAL's own error code, when it gave one. The difference between "your session
 *  expired", "the browser blocked the renewal iframe" and "this account cannot have
 *  this token" is the whole diagnosis, and the old message threw all three away. */
function msalCode(err: unknown): string | null {
  const e = err as { errorCode?: unknown; message?: unknown } | null;
  const code = typeof e?.errorCode === 'string' ? e.errorCode.trim() : '';
  if (code) return code;
  const msg = typeof e?.message === 'string' ? e.message : '';
  const m = msg.match(/\b(interaction_required|login_required|consent_required|invalid_grant|monitor_window_timeout|token_renewal_iframe_timed_out|no_account_error|[a-z]+_required)\b/);
  return m ? m[1] : null;
}

export async function acquireApiToken(): Promise<string> {
  const all = msalInstance.getAllAccounts();
  const active = msalInstance.getActiveAccount();
  const account =
    (active && active.tenantId === MAJANS_TENANT_ID ? active : null)
    ?? all.find((a) => a.tenantId === MAJANS_TENANT_ID)
    ?? null;

  if (!account) {
    // Deliberately NOT `?? all[0]`, which is what this used to do. An account from
    // another tenant can NEVER be issued a token for api://<clientId>, so falling
    // back to one converts a fixable "you are on the wrong account" into a silent
    // renewal that fails for ever: the app retries, every page reads
    // "Re-authenticating…", and nothing anywhere says the ACCOUNT is the problem.
    // Naming the accounts we can actually see is the difference between a dead end
    // and a thirty-second fix.
    if (all.length > 0) {
      throw new Error(
        `Signed in as ${all.map((a) => a.username).filter(Boolean).join(', ')}, which is not a Majans `
        + 'account, so no token for this app can be issued. Sign out and sign back in with your '
        + '@majans.com account.',
      );
    }
    throw new Error('Not signed in');
  }
  // Make the resolved account sticky, so the next call does not have to rediscover
  // it and a stray foreign account cannot win a later race.
  if (active !== account) msalInstance.setActiveAccount(account);

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiScopes, account });
    return result.accessToken;
  } catch (err) {
    const code = msalCode(err);
    const lastRedirect = Number(sessionStorage.getItem(TOKEN_REDIRECT_KEY) || 0);
    if (!tokenRedirectInFlight && Date.now() - lastRedirect > 60_000) {
      tokenRedirectInFlight = true;
      sessionStorage.setItem(TOKEN_REDIRECT_KEY, String(Date.now()));
      console.warn('[auth] silent token acquisition failed; redirecting to re-authenticate', err);
      try {
        await msalInstance.acquireTokenRedirect({ ...apiScopes, account });
      } catch (redirectErr) {
        tokenRedirectInFlight = false;
        console.error('[auth] interactive token redirect failed', redirectErr);
      }
    }
    // Carry the reason onto the screen. A bare "Re-authenticating…" is
    // indistinguishable from a hang, and it was reported three times in one day as
    // "the app is broken" while every API route was returning 200 — nobody could
    // tell the difference between a stale token, a blocked iframe and a wrong
    // account, because the app said the same six words for all of them.
    throw new Error(
      code
        ? `Re-authenticating… (${code}) — if this does not clear, sign out and sign back in.`
        : 'Re-authenticating… — if this does not clear, sign out and sign back in.',
    );
  }
}
